import React from "react";
import { Phone, MessageCircle, MapPin, Mail, Clock } from "lucide-react";
import { COMPANY, waLink, telLink, NEIGHBORHOODS } from "../lib/contact";

const LINKS = [
  { label: "Início", href: "#inicio" },
  { label: "Sobre", href: "#sobre" },
  { label: "Serviços", href: "#servicos" },
  { label: "Diferenciais", href: "#diferenciais" },
  { label: "Atendimento", href: "#atendimento" },
  { label: "FAQ", href: "#faq" },
];

export const Footer = () => {
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#0f172a] text-white"
    >
      <div className="hazard-bar" />

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-14">
        <div className="grid md:grid-cols-12 gap-10">
          {/* Brand */}
          <div className="md:col-span-4">
            <div className="flex items-center gap-3 mb-4">
              <img
                src="https://customer-assets.emergentagent.com/job_04c778d5-60fa-4c63-9f95-1580f724c05b/artifacts/yr2zuvns_logo.png"
                alt="PJ Caçambas de Entulho"
                className="h-14 w-auto"
                loading="lazy"
              />
              <div>
                <h3 className="font-display text-2xl text-[#fff505] leading-none">
                  PJ Caçambas
                </h3>
                <p className="text-xs uppercase tracking-widest text-white/70 mt-1">
                  Disk Entulho · Jundiaí
                </p>
              </div>
            </div>
            <p className="text-white/70 leading-relaxed text-sm font-medium">
              Aluguel de caçambas, remoção de entulho e descarte de resíduos em
              Jundiaí e Região. Atendimento rápido, preço justo e entrega
              imediata.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp-btn"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#25d366] text-white text-sm font-extrabold uppercase border-2 border-[#fff505]"
              >
                <MessageCircle className="w-4 h-4" strokeWidth={3} />
                WhatsApp
              </a>
              <a
                href={telLink}
                data-testid="footer-phone-btn"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#fff505] text-[#0f172a] text-sm font-extrabold uppercase border-2 border-[#fff505]"
              >
                <Phone className="w-4 h-4" strokeWidth={3} />
                Ligar
              </a>
            </div>
          </div>

          {/* Links */}
          <div className="md:col-span-2">
            <h4 className="font-display text-xl text-[#fff505] mb-4">Links rápidos</h4>
            <ul className="space-y-2">
              {LINKS.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    data-testid={`footer-link-${l.label.toLowerCase()}`}
                    className="text-white/80 hover:text-[#fff505] text-sm font-bold uppercase tracking-wide"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contato */}
          <div className="md:col-span-3">
            <h4 className="font-display text-xl text-[#fff505] mb-4">Contato</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-[#fff505] mt-0.5" strokeWidth={3} />
                <a
                  href={telLink}
                  data-testid="footer-phone-link"
                  className="font-bold hover:text-[#fff505]"
                >
                  {COMPANY.phoneDisplay}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MessageCircle className="w-4 h-4 text-[#fff505] mt-0.5" strokeWidth={3} />
                <a
                  href={waLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp-link"
                  className="font-bold hover:text-[#fff505]"
                >
                  WhatsApp 24h
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-[#fff505] mt-0.5" strokeWidth={3} />
                <a
                  href={`mailto:${COMPANY.email}`}
                  data-testid="footer-email-link"
                  className="font-bold hover:text-[#fff505] break-all"
                >
                  {COMPANY.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#fff505] mt-0.5" strokeWidth={3} />
                <span className="font-bold">{COMPANY.region}</span>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-[#fff505] mt-0.5" strokeWidth={3} />
                <span className="font-bold">Seg a Sáb · 07h às 19h</span>
              </li>
            </ul>
          </div>

          {/* Cobertura */}
          <div className="md:col-span-3">
            <h4 className="font-display text-xl text-[#fff505] mb-4">Cobertura</h4>
            <div className="border-2 border-[#fff505] bg-[#fff505]/5 p-4">
              <p className="font-display text-3xl text-[#fff505] leading-none">
                Jundiaí
              </p>
              <p className="text-xs uppercase tracking-widest text-white/70 mt-2 font-extrabold">
                São Paulo · SP
              </p>
              <p className="text-xs text-white/60 mt-3 leading-relaxed font-medium">
                Atendimento exclusivo em Jundiaí — todos os bairros, atendimento
                rápido e entrega no mesmo dia.
              </p>
            </div>
          </div>
        </div>

        {/* SEO neighborhoods strip */}
        <div className="mt-10 pt-6 border-t border-white/10">
          <p className="text-[11px] uppercase tracking-[0.2em] font-extrabold text-[#fff505] mb-3">
            Bairros atendidos em Jundiaí
          </p>
          <p className="text-xs text-white/60 leading-relaxed font-medium">
            {NEIGHBORHOODS.join(" · ")}
          </p>
        </div>

        {/* SEO keyword strip */}
        <div className="mt-6 pt-6 border-t border-white/10">
          <p className="text-[11px] uppercase tracking-[0.2em] font-extrabold text-[#fff505] mb-3">
            Soluções em entulho
          </p>
          <p className="text-xs text-white/60 leading-relaxed font-medium">
            Caçamba de entulho em Jundiaí · Aluguel de caçamba Jundiaí · Disk
            entulho Jundiaí · Caçamba para obra Jundiaí · Remoção de entulho
            Jundiaí · Caçamba perto de mim · Tele entulho Jundiaí · Caçamba para
            reforma · Caçamba preço Jundiaí · Caçamba estacionária · Coleta de
            entulho · Limpeza pós obra · Retirada de entulho · Caçamba com
            entrega rápida
          </p>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-5 flex flex-col md:flex-row gap-3 items-center justify-between text-xs text-white/60">
          <p>
            © {year} {COMPANY.name}. Todos os direitos reservados.
          </p>
          <p className="font-bold">
            CNPJ disponível mediante solicitação · {COMPANY.region}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
